import { CircularProgress } from '@material-ui/core'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { confirmLineItems, deleteLineItems, orderSelectors } from 'ducks/orderComponents'
import { permissionsSelectors } from 'ducks/permissions'
import Button from 'elements/button/Button'
import { styled, Tooltip } from 'opensolar-ui'
import type LineItemType from 'pages/ordering/OrderLineItem'
import { OrderLineItem } from 'pages/ordering/OrderLineItem'
import { useAddedToCartToast } from 'pages/ordering/addedToCartToast'
import ProjectOrderPresenter from 'pages/ordering/ProjectOrderPresenter/projectOrderPresenter'
import useProjectOrderPresenter from 'pages/ordering/ProjectOrderPresenter/useProjectOrderPresenter'
import { getOrderCost, getResolvedSource } from 'pages/ordering/utils'
import { useTranslate } from 'ra-core'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import type { ComponentTypes, ComponentTypesV2 } from 'types/selectComponent'
import ProjectBomTable from './ProjectBomTable'
import ShopItemsTable from './ShopItemsTable'
import useOrderByProjectCartData, { type LineItemWithResolvedSource } from './useOrderByProjectCartData'

const Container = styled('div')({
  padding: 25,
  maxWidth: 1200,
  margin: '0 auto',
})

const Header = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 24,
})

const Title = styled('h1')({
  fontSize: 24,
  fontWeight: 600,
  margin: 0,
})

const Subtitle = styled('p')(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.grey[600],
  margin: '4px 0 0 0',
}))

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 200,
})

const EmptyState = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 200,
  backgroundColor: theme.palette.grey[100],
  borderRadius: 8,
  padding: 40,
}))

const EmptyStateText = styled('p')(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.grey[600],
  marginBottom: 16,
}))

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 16,
  marginTop: 24,
  paddingTop: 24,
  borderTop: `1px solid ${theme.palette.grey[200]}`,
}))

const OrderByProjectCartPage = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const translate = useTranslate()
  const projectOrderPresenter = useProjectOrderPresenter()
  const shopHardwareAccess = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'shop_hardware_access')
  )
  const allowEdit = shopHardwareAccess?.allowEdit ?? false
  const {
    groupedLineItems,
    nonProjectLineItems,
    confirmedLineItems,
    isLoading,
    hasLineItems,
  } = useOrderByProjectCartData()
  const { showAddedToCartToast } = useAddedToCartToast()
  const unconfirmedLineItems = useSelector(orderSelectors.getUnconfirmedLineItems)

  // Single combined list of all project line items (each item appears once).
  // Display only: we do not mutate or clone. Each line item still has projectOrder
  // (projectId, systemId, requiredQuantity) for checkout, PartsList badges, and allocations.
  const allProjectLineItems = useMemo(
    () => unconfirmedLineItems.filter((li: LineItemType) => li.projectOrder && li.projectOrder.length > 0),
    [unconfirmedLineItems]
  )

  // One synthetic group for the combined table; use first project for "Add Item" context
  const combinedProjectGroup = useMemo(() => {
    const first = groupedLineItems[0]
    const firstProjectOrder = allProjectLineItems[0]?.projectOrder?.[0]
    const lineItemsWithResolvedSource = allProjectLineItems.map((li) => ({
      ...li,
      resolvedSource: getResolvedSource(li.selectedDistributor, li.orderingData),
    })) as LineItemWithResolvedSource[]
    return {
      projectId: first?.projectId ?? firstProjectOrder?.projectId ?? 0,
      systemId: first?.systemId ?? firstProjectOrder?.systemId,
      projectInfo: undefined,
      lineItems: lineItemsWithResolvedSource,
      title: 'Order items',
    }
  }, [groupedLineItems, allProjectLineItems])

  const projectCount = useMemo(
    () =>
      new Set(allProjectLineItems.flatMap((li) => li.projectOrder?.map((po) => po.projectId) ?? [])).size,
    [allProjectLineItems]
  )

  // Filter to only include line items that are orderable (available stock and quantity > 0)
  const orderableLineItems = useMemo(() => {
    return unconfirmedLineItems.filter((lineItem: LineItemType) => {
      return (
        ProjectOrderPresenter.getStockLevelStatusFromLineItem(lineItem) !== 'not_available' && lineItem.quantity > 0
      )
    })
  }, [unconfirmedLineItems])

  useEffect(() => {
    logAmplitudeEvent('page_viewed', {
      page: 'order_by_project_cart',
    })
  }, [])

  const handleBack = () => {
    history.push('/multi_project_ordering')
  }

  const confirmOrderableItems = useCallback(() => {
    const lineItemUuids = orderableLineItems.map((lineItem: LineItemType) => lineItem.uuid)
    dispatch(
      confirmLineItems({
        uuids: lineItemUuids,
        confirmed: true,
      })
    )
    return lineItemUuids
  }, [dispatch, orderableLineItems])

  const handleAddToCart = useCallback(() => {
    const lineItemUuids = confirmOrderableItems()
    const itemCount = ProjectOrderPresenter.getLineItemsTotalQuantity(orderableLineItems)
    const subtotal = getOrderCost(orderableLineItems)
    showAddedToCartToast({
      variant: 'project',
      project: {
        label: translate('Multi-Project Order'),
        itemCount,
        subtotal,
        location: 'multi_project_ordering',
      },
    })
    logAmplitudeEvent('generic_button_clicked', {
      source: 'order_by_project_cart',
      context: 'add_to_cart',
      projectCount,
      totalLineItems: lineItemUuids.length,
    })
  }, [confirmOrderableItems, orderableLineItems, projectCount, showAddedToCartToast, translate])

  const handleProceedToCheckout = useCallback(() => {
    // First, clear any previously confirmed items from the cart to avoid duplicates
    // This ensures only the Order By Project items are shown in checkout
    dispatch(deleteLineItems({ source: 'confirmed', identifiers: [true] }))

    const lineItemUuids = confirmOrderableItems()

    logAmplitudeEvent('generic_button_clicked', {
      source: 'order_by_project_cart',
      context: 'proceed_to_checkout',
      projectCount,
      totalLineItems: lineItemUuids.length,
    })

    // Navigate to checkout
    history.push('/checkout')
  }, [dispatch, confirmOrderableItems, projectCount, history])

  const handleAddComponent = useCallback(
    (component: { [key: string]: any }, projectId: number, systemId?: number) => {
      const newLineItem = new OrderLineItem({
        code: component.code,
        quantity: component.quantity || 1,
        componentType: component.componentType as ComponentTypes,
        componentTypeV2: component.componentTypeV2 as ComponentTypesV2,
        selectedDistributor: component.source === 'component' ? 'component' : component.source,
        status: 'loading',
        confirmed: true,
        projectOrder: [
          {
            projectId,
            systemId,
            requiredQuantity: component.quantity || 1,
          },
        ],
      })

      projectOrderPresenter.addLineItemsAndConfirm([newLineItem])

      logAmplitudeEvent('generic_button_clicked', {
        source: 'order_by_project_cart',
        context: 'add_component',
        componentCode: component.code,
        projectId,
      })
    },
    [projectOrderPresenter]
  )

  const handleAddShopItem = useCallback(
    (component: { [key: string]: any }) => {
      const newLineItem = new OrderLineItem({
        code: component.code,
        quantity: component.quantity || 1,
        componentType: component.componentType as ComponentTypes,
        componentTypeV2: component.componentTypeV2 as ComponentTypesV2,
        selectedDistributor: component.source === 'component' ? 'component' : component.source,
        status: 'loading',
        confirmed: true,
        // No projectOrder for shop items
      })

      projectOrderPresenter.addLineItemsAndConfirm([newLineItem])

      logAmplitudeEvent('generic_button_clicked', {
        source: 'order_by_project_cart',
        context: 'add_shop_item',
        componentCode: component.code,
      })
    },
    [projectOrderPresenter]
  )

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </Container>
    )
  }

  if (!hasLineItems) {
    return (
      <Container>
        <Header>
          <div>
            <Title>Review Your Order</Title>
          </div>
        </Header>
        <EmptyState>
          <EmptyStateText>No items in your cart. Select projects to add items.</EmptyStateText>
          <Button color="primary" onClick={handleBack}>
            Back to Projects
          </Button>
        </EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>Review Your Order</Title>
          <Subtitle>
            {confirmedLineItems.length > 0 && (
              <>
                {confirmedLineItems.length} item{confirmedLineItems.length !== 1 ? 's' : ''} in cart
              </>
            )}
            {confirmedLineItems.length > 0 && (allProjectLineItems.length > 0 || nonProjectLineItems.length > 0) && ' • '}
            {allProjectLineItems.length > 0 && (
              <>
                {allProjectLineItems.length} item{allProjectLineItems.length !== 1 ? 's' : ''}
              </>
            )}
            {allProjectLineItems.length > 0 && nonProjectLineItems.length > 0 && ' • '}
            {nonProjectLineItems.length > 0 && (
              <>
                {nonProjectLineItems.length} shop item{nonProjectLineItems.length !== 1 ? 's' : ''}
              </>
            )}
          </Subtitle>
        </div>
      </Header>

      {confirmedLineItems.length > 0 && (
        <ShopItemsTable
          lineItems={confirmedLineItems}
          onAddComponent={handleAddShopItem}
          title="In your cart"
          subtitle="Items already in your cart"
          hideAddRow
          allowEdit={allowEdit}
        />
      )}

      {allProjectLineItems.length > 0 && (
        <ProjectBomTable
          group={combinedProjectGroup}
          onAddComponent={handleAddComponent}
          allowEdit={allowEdit}
        />
      )}

      {nonProjectLineItems.length > 0 && (
        <ShopItemsTable
          lineItems={nonProjectLineItems}
          onAddComponent={handleAddShopItem}
          allowEdit={allowEdit}
        />
      )}

      <Actions>
        <Button onClick={handleBack}>Back to Projects</Button>
        <Tooltip title={!allowEdit ? translate('No permission') : ''}>
          <span>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAddToCart}
              disabled={!allowEdit || orderableLineItems.length === 0}
            >
              Add to Cart
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={!allowEdit ? translate('No permission') : ''}>
          <span>
            <Button
              color="primary"
              onClick={handleProceedToCheckout}
              disabled={!allowEdit || orderableLineItems.length === 0}
            >
              Proceed to Checkout
            </Button>
          </span>
        </Tooltip>
      </Actions>
    </Container>
  )
}

export default OrderByProjectCartPage